// Google Ads appends a campaign's tracking template (`{lpurl}?utm_…`) after a
// final URL's fragment, so `/da#pricing` lands as `#pricing?utm_source=…`.
// These helpers find the section id in such a hash. Plain hashes are left to
// the browser. Failure modes: tests/hash-query-scroll/failure-modes.md

// A letter, then letters, digits, `-` or `_`: every section id the landing
// pages link to, and nothing a selector or markup could be built from.
const SECTION_ID = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;

// Up to this many pixels the page still counts as "at the top".
const TOP_TOLERANCE_PX = 2;

/** The id in `#<id>?…` or `#<id>&…`, or null (plain hash, no id, unsafe id). */
export function parseHashQueryId(hash: unknown): string | null {
  if (typeof hash !== "string" || !hash.startsWith("#")) return null;
  const body = hash.slice(1);
  const cut = body.search(/[?&]/);
  if (cut <= 0) return null;
  const id = body.slice(0, cut);
  return SECTION_ID.test(id) ? id : null;
}

interface HashQueryScrollInput {
  hash: unknown;
  scrollY: number;
  hasElement: (id: string) => boolean;
  /** `PerformanceNavigationTiming.type` of the page load, if known. */
  navigationType?: string;
}

/** The id to scroll to, or null to leave the page as the browser put it. */
export function decideHashQueryScroll({
  hash,
  scrollY,
  hasElement,
  navigationType,
}: HashQueryScrollInput): string | null {
  const id = parseHashQueryId(hash);
  if (!id) return null;
  // On reload and back/forward the browser restores the visitor's own
  // position for a plain hash too, even at the top: keep it.
  if (navigationType === "reload" || navigationType === "back_forward") return null;
  // Restored scroll positions and visitors who already scrolled stay put.
  if (!(scrollY <= TOP_TOLERANCE_PX)) return null;
  return hasElement(id) ? id : null;
}

interface RepeatOnLoadInput {
  hasInteracted: boolean;
  scrollY: number;
  scrolledTo: number;
}

/**
 * After `load`, images above the section may have moved it. Repeat the jump
 * only while the visitor hasn't touched the page and it is still where the
 * first jump put it.
 */
export function shouldRepeatOnLoad({
  hasInteracted,
  scrollY,
  scrolledTo,
}: RepeatOnLoadInput): boolean {
  if (hasInteracted) return false;
  return Math.abs(scrollY - scrolledTo) <= TOP_TOLERANCE_PX;
}
